import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Trans, useTranslation } from "react-i18next";

import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import { useQuery } from "@tanstack/react-query";

import Title from "../../components/Title";
import BackButton from "../../components/BackButton";
import Modal from "../../components/Modal";

import { usePaxtonContext } from "../../context/Paxton";

import { client } from "../../bit/board/hooks/useGraphQLClient";
import { useTimeoutFunction } from "../../lib/hooks/useTimeoutFunction";

import { QUERY_ACCESS_EVENT } from "../../config/graphql/query";
import { MUTATION_SEND_DOOR_OPENED_NOTIFICATION } from "../../config/graphql/mutation";

const DoorControl = () => {
  const { t } = useTranslation(["door"]);

  const { id } = useParams<{ id: string }>();

  const navigate = useNavigate();

  const { openDoor } = usePaxtonContext();

  const [status, setStatus] = useState<"error" | "loading" | "success">();

  const { data: accessEventData } = useQuery(
    [`access/event/${id}`],
    () =>
      client.request<{ accessEvent: IAccessEvent }>(QUERY_ACCESS_EVENT, {
        id,
      }),
    {
      retry: false,
      staleTime: 1 * (60 * 1000),
      onError: () => {
        navigate("/", { replace: true });
      },
    },
  );

  const onOpenDoor = async () => {
    setStatus(() => "loading");
    try {
      const status = await openDoor();

      if (status && accessEventData?.accessEvent.type === "MeetingAttendee") {
        try {
          await client.request(MUTATION_SEND_DOOR_OPENED_NOTIFICATION, { id });
        } catch (mutationError) {
          console.log("Failed to notify attendee:", mutationError);
        }
      }

      setStatus(() => (status ? "success" : "error"));
    } catch (error) {
      setStatus(() => "error");
    }
  };

  const onDismiss = () => {
    if (status === "error") {
      setStatus(() => undefined);
      return;
    }

    navigate("/", { replace: true });
  };

  const timeout = useMemo(() => {
    if (status === "error") {
      return 10000;
    }

    if (status === "success") {
      return 7000;
    }

    return 0;
  }, [status]);

  useTimeoutFunction(onDismiss, timeout);

  return (
    <div className="d-flex align-items-center flex-column">
      <BackButton to="/" />
      <Title className="page-title home mb-5">
        <Typography variant="h1">{t(`door:control.title`)}</Typography>
        <Typography variant="h3" className="font-weight-regular mt-3">
          {t(`door:control.subtitle`)}
        </Typography>
      </Title>
      <Button variant="contained" className="mt-4" onClick={onOpenDoor}>
        {t(`door:control.openBtn`)}
      </Button>

      {status && (
        <Modal
          status={status}
          title={<Trans i18nKey={`door:status.${status}.title`} />}
          message={<Trans i18nKey={`door:status.${status}.message`} />}
          onDismiss={onDismiss}
        />
      )}
    </div>
  );
};

export default DoorControl;
